import {
  keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { FISCAL_YEAR_QUERY_KEY } from "@/shared/hooks/useFiscalYearQuery";
import { ACCOUNTING_QUERY_KEYS } from "../configs/queryKeys";
import { accountingService } from "../services/accounting.service";
import type {
  SepaRemittanceEventInput,
  SepaBankTreatmentInput,
  BankMovementAccountsQueryParams,
  BankMovementFusionCandidatesQueryParams,
  BankMovementPaymentModesQueryParams,
  BankMovementsQueryParams,
  CreateRemisePayload,
  SepaExportPayload,
  SepaRemittancesQueryParams,
  LedgerAllocationCategorizationRuleCreatePayload,
  LedgerAllocationCategorizationRulesQueryParams,
  LedgerAllocationCategorizationRuleUpdatePayload,
  LedgerAllocationsQueryParams,
  LedgerAllocationOperationTypesQueryParams,
  LedgerAllocationWritePayload,
  BankMovementWritePayload,
  BankAccountWritePayload,
  SettlementsQueryParams,
  SettlementWritePayload,
  SynthesisQueryParams,
  SynthesisDetailQueryParams,
  AccountingBudgetsQueryParams,
  AccountingBudgetUpsertPayload,
  AccountingSettingsUpdatePayload,
} from "../types/accounting.types";

/**
 * Settlement operations can change the status of linked entities (refund requests, ecom orders).
 * Call this after any settlement or settlement-allocation mutation to keep those caches in sync.
 */
const invalidateLinkedEntityCaches = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
  queryClient.invalidateQueries({ queryKey: ["ecom-orders"] });
  queryClient.invalidateQueries({ queryKey: ["yeati-loc-bookings"] });
};

export const useBankMovementAccountsQuery = (params: BankMovementAccountsQueryParams, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementAccountsList(params),
    queryFn: () => accountingService.listBankMovementAccounts(params),
    enabled,
  });
};

export const useBankMovementsQuery = (params: BankMovementsQueryParams, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementsList(params),
    queryFn: () => accountingService.listBankMovements(params),
    enabled,
  });
};

export const useBankMovementDetailQuery = (id: string | null, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementDetail(id ?? ""),
    queryFn: () => accountingService.getBankMovement(id!),
    enabled: enabled && Boolean(id),
  });
};

export const useBankMovementPaymentModesQuery = (params: BankMovementPaymentModesQueryParams, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementPaymentModesList(params),
    queryFn: () => accountingService.listBankMovementPaymentModes(params),
    enabled,
  });
};

export const useBankMovementFusionCandidatesQuery = (
  params: BankMovementFusionCandidatesQueryParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementFusionCandidatesList(params),
    queryFn: () => accountingService.listBankMovementFusionCandidates(params),
    enabled,
  });
};

type BankMovementsInfiniteParams = Omit<BankMovementsQueryParams, "page">;

export const useInfiniteBankMovementsQuery = (params: BankMovementsInfiniteParams, enabled = true) => {
  return useInfiniteQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.bankMovementsList(params), "infinite"],
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => accountingService.listBankMovements({
      ...params,
      page: Number(pageParam),
    }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metadata?.current_page ?? 1;
      const totalPages = lastPage.metadata?.total_pages ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

export const useLedgerAllocationsQuery = (params: LedgerAllocationsQueryParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationsList(params),
    queryFn: () => accountingService.listLedgerAllocations(params),
    enabled,
  });
};

type LedgerAllocationsInfiniteParams = Omit<LedgerAllocationsQueryParams, "page">;

export const useInfiniteLedgerAllocationsQuery = (
  params: LedgerAllocationsInfiniteParams,
  enabled: boolean = true,
) => {
  return useInfiniteQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocationsList(params), "infinite"],
    initialPageParam: 1,
    enabled,
    queryFn: ({ pageParam }) => accountingService.listLedgerAllocations({
      ...params,
      page: Number(pageParam),
    }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metadata?.current_page ?? 1;
      const totalPages = lastPage.metadata?.total_pages ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

export const useLedgerAllocationOperationTypesQuery = (
  params: LedgerAllocationOperationTypesQueryParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types", params],
    queryFn: () => accountingService.listLedgerAllocationOperationTypes(params),
    enabled,
  });
};

export const useLedgerOperationTypeFacetsQuery = (
  params: LedgerAllocationsQueryParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-type-facets", params],
    queryFn: () => accountingService.getLedgerOperationTypeFacets(params),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useLedgerAllocationCategorizationRulesQuery = (
  params: LedgerAllocationCategorizationRulesQueryParams = {},
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationCategorizationRulesList(params),
    queryFn: () => accountingService.listLedgerAllocationCategorizationRules(params),
    enabled,
  });
};

export const useCreateLedgerAllocationCategorizationRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LedgerAllocationCategorizationRuleCreatePayload) =>
      accountingService.createLedgerAllocationCategorizationRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationCategorizationRules });
    },
  });
};

export const useUpdateLedgerAllocationCategorizationRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LedgerAllocationCategorizationRuleUpdatePayload) =>
      accountingService.updateLedgerAllocationCategorizationRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationCategorizationRules });
    },
  });
};

export const useDeleteLedgerAllocationCategorizationRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountingService.deleteLedgerAllocationCategorizationRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationCategorizationRules });
    },
  });
};

export const useCreateLedgerAllocation = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LedgerAllocationWritePayload) => accountingService.createLedgerAllocation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationsList({ bankMovementId }) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

export const useUpdateLedgerAllocation = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string, payload: LedgerAllocationWritePayload }) =>
      accountingService.updateLedgerAllocation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationsList({ bankMovementId }) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

export const useDeleteLedgerAllocation = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (target: string | { id: string, temporaryOnly: true }) => typeof target === "string"
      ? accountingService.deleteLedgerAllocation(target)
      : accountingService.discardTemporaryAllocation(target.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationsList({ bankMovementId }) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

// ADM-681 : recalcule le solde d'affectation côté serveur (BalanceRecalculator)
// et resynchronise les listes de mouvements / d'affectations. Sert de filet de
// sécurité à l'ouverture du drawer d'un mouvement flagué en écart.
export const useRecalculateBankMovementBalance = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountingService.recalculateBankMovementBalance(bankMovementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationsList({ bankMovementId }) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

// DEC-15 : les deux issues de l'arbitrage d'un écart bancaire, et le compteur
// qui les annonce. Les trois invalident `bankMovements` : arbitrer une ligne la
// sort de la file, et accepter le montant banque change aussi son solde
// d'affectation, donc la liste et les agrégats de la page.

export const useBankMovementsDiscrepancySummaryQuery = (enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementsDiscrepancySummary,
    queryFn: () => accountingService.getBankMovementsDiscrepancySummary(),
    enabled,
  });
};

export const useAcceptBankMovementBankAmount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bankMovementId: string) => accountingService.acceptBankMovementBankAmount(bankMovementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

export const useKeepBankMovementOwnAmount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bankMovementId, expectedBankCredit, expectedBankDebit }: {
      bankMovementId: string
      expectedBankCredit: number | null
      expectedBankDebit: number | null
    }) => accountingService.keepBankMovementOwnAmount(bankMovementId, { bankCredit: expectedBankCredit, bankDebit: expectedBankDebit }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

// DEC-15, second volet : les doublons que la tâche de nettoyage écarte, et les
// trois issues de leur arbitrage. Les trois invalident `bankMovements` : deux
// d'entre elles suppriment un mouvement, donc la liste, les agrégats et cette
// file bougent ensemble.

export const useBankMovementDuplicateCandidatesQuery = (enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankMovementDuplicateCandidates,
    queryFn: () => accountingService.listBankMovementDuplicateCandidates(),
    enabled,
  });
};

const useDuplicateResolution = (resolve: (bankMovementId: string) => Promise<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resolve,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

export const useReattachBankMovementDuplicate = () =>
  useDuplicateResolution((id) => accountingService.reattachBankMovementDuplicate(id));

export const useDeleteBankMovementDuplicate = () =>
  useDuplicateResolution((id) => accountingService.deleteBankMovementDuplicate(id));

export const useDismissBankMovementDuplicate = () =>
  useDuplicateResolution((id) => accountingService.dismissBankMovementDuplicate(id));

export const useFuseBankMovement = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (candidateId: string) => accountingService.fuseBankMovement(bankMovementId, candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovementFusionCandidatesList({ bankMovementId }) });
    },
  });
};

export const useMergeBankMovements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof accountingService.mergeBankMovements>[0]) =>
      accountingService.mergeBankMovements(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
    },
  });
};

export const useSplitBankMovement = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountingService.splitBankMovement(bankMovementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovementFusionCandidatesList({ bankMovementId }) });
    },
  });
};

export const useUpdateBankMovement = (bankMovementId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BankMovementWritePayload) =>
      accountingService.updateBankMovement(bankMovementId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useCreateBankMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BankMovementWritePayload = {}) =>
      accountingService.createBankMovement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

export const useDeleteBankMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bankMovementId: string) =>
      accountingService.deleteBankMovement(bankMovementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useCreateLedgerAllocationOperationType = (
  bankMovementId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name, budgetType, nature, bankMovementId: overrideBankMovementId,
    }: {
      name: string
      budgetType?: string | null
      nature?: "expense" | "revenue"
      bankMovementId?: string
    }) =>
      accountingService.createLedgerAllocationOperationType({
        bankMovementId: overrideBankMovementId || bankMovementId,
        budgetType,
        name,
        nature,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types"] });
      // Miroir de invalidateOrgOperationTypeCaches : la page Budgets lit la
      // liste org-scopée, elle doit voir ce qui est édité depuis un drawer.
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypes });
    },
  });
};

export const useUpdateLedgerAllocationOperationType = (
  bankMovementId: string,
  budgetType?: string | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      operationTypeId, name, actif, numeroCompte,
    }: {
      operationTypeId: string
      name?: string
      actif?: boolean
      numeroCompte?: string | null
    }) =>
      accountingService.updateLedgerAllocationOperationType({
        bankMovementId,
        budgetType,
        operationTypeId,
        name,
        actif,
        numeroCompte,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types"] });
      // Miroir de invalidateOrgOperationTypeCaches : la page Budgets lit la
      // liste org-scopée, elle doit voir ce qui est édité depuis un drawer.
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypes });
    },
  });
};

export const useDeleteLedgerAllocationOperationType = (
  bankMovementId: string,
  budgetType?: string | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operationTypeId: string) =>
      accountingService.deleteLedgerAllocationOperationType({
        bankMovementId,
        budgetType,
        operationTypeId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types"] });
      // Miroir de invalidateOrgOperationTypeCaches : la page Budgets lit la
      // liste org-scopée, elle doit voir ce qui est édité depuis un drawer.
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypes });
    },
  });
};

export const useMergeLedgerAllocationOperationType = (
  bankMovementId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ operationTypeId, targetOperationTypeId }: { operationTypeId: string, targetOperationTypeId: string }) =>
      accountingService.mergeLedgerAllocationOperationType({
        bankMovementId,
        operationTypeId,
        targetOperationTypeId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types"] });
      // Miroir de invalidateOrgOperationTypeCaches : la page Budgets lit la
      // liste org-scopée, elle doit voir ce qui est édité depuis un drawer.
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypes });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocationCategorizationRules });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.synthesis });
    },
  });
};

// ── Settlements ──────────────────────────────────────────

export const useSettlementQuery = (settlementId: string | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.settlementDetail(settlementId ?? ""),
    queryFn: () => accountingService.getSettlement(settlementId!),
    enabled: enabled && Boolean(settlementId),
  });
};

export const useSettlementsQuery = (params: SettlementsQueryParams, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.settlementsList(params),
    queryFn: () => accountingService.listSettlements(params),
    enabled,
  });
};

type SettlementsInfiniteParams = Omit<SettlementsQueryParams, "page">;

export const useInfiniteSettlementsQuery = (params: SettlementsInfiniteParams) => {
  return useInfiniteQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.settlementsList(params), "infinite"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => accountingService.listSettlements({
      ...params,
      page: Number(pageParam),
    }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metadata?.current_page ?? 1;
      const totalPages = lastPage.metadata?.total_pages ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

export const useSettlementAccountsQuery = (params: { context?: string } = {}) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.settlementAccountsList(params),
    queryFn: () => accountingService.listSettlementAccounts(params),
  });
};

// ADM-609: org-scoped stock families/references for the Grand Livre filters.
// Distinct from the bank-movement-scoped variants used by the allocation drawer.
export const useLedgerStockFamiliesQuery = (category?: string | null, stockOnly?: boolean) => {
  return useQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "stock-families", category ?? "all", stockOnly ?? false],
    queryFn: () => accountingService.listLedgerStockFamilies(category ?? undefined, stockOnly),
  });
};

export const useLedgerStockReferencesQuery = (
  familyId?: string | null,
  category?: string | null,
  stockOnly?: boolean,
) => {
  return useQuery({
    queryKey: [
      ...ACCOUNTING_QUERY_KEYS.ledgerAllocations,
      "stock-references", familyId ?? "all", category ?? "all", stockOnly ?? false,
    ],
    queryFn: () => accountingService.listLedgerStockReferences(
      familyId ?? undefined,
      category ?? undefined,
      stockOnly,
    ),
  });
};

export const useSettlementPaymentMethodsQuery = (params: { context?: string } = {}) => {
  return useQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.settlementPaymentMethods, params],
    queryFn: () => accountingService.listSettlementPaymentMethods(params),
  });
};

export const useCreateSettlement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SettlementWritePayload) => accountingService.createSettlement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useUpdateSettlement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string, payload: SettlementWritePayload }) =>
      accountingService.updateSettlement(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useDeleteSettlement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountingService.deleteSettlement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useCreateSettlementWithMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { settlement: SettlementWritePayload, bankMovement: BankMovementWritePayload }) =>
      accountingService.createSettlementWithMovement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useSettlementAllocationsQuery = (settlementId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.settlementAllocationsList(settlementId),
    queryFn: () => accountingService.listSettlementAllocations(settlementId),
    enabled: enabled && Boolean(settlementId),
  });
};

export const useCreateSettlementLedgerAllocation = (settlementId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LedgerAllocationWritePayload) => accountingService.createLedgerAllocation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlementAllocationsList(settlementId) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useUpdateSettlementLedgerAllocation = (settlementId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string, payload: LedgerAllocationWritePayload }) =>
      accountingService.updateLedgerAllocation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlementAllocationsList(settlementId) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useDeleteSettlementLedgerAllocation = (settlementId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountingService.deleteLedgerAllocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlementAllocationsList(settlementId) });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useCreateRemise = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRemisePayload) => accountingService.createRemise(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

// ── SEPA Export ──────────────────────────────────────────

/** YTI-1870 : les mouvements du compte écartés de la remise, et pourquoi. */
export const useSepaEligibilityQuery = (bankAccountId: number | null) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.sepaEligibility(bankAccountId ?? 0),
    queryFn: () => accountingService.sepaEligibility(bankAccountId as number),
    enabled: bankAccountId !== null,
  });
};

export const useSepaExport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SepaExportPayload) => accountingService.sepaExport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.sepaRemittances });
    },
  });
};

// ── SEPA remittances (YTI-1732) ──────────────────────────

// Existence only, scoped to the selected CSE and invalidated after the first export.
export const useHasSepaRemittancesQuery = (organizationId: string | undefined, enabled: boolean) => useQuery({
  queryKey: [...ACCOUNTING_QUERY_KEYS.sepaRemittances, "exists", organizationId],
  queryFn: async () => (await accountingService.listSepaRemittances({ limit: 1, page: 1 })).sepaRemittances.length > 0,
  enabled: enabled && Boolean(organizationId),
});

type SepaRemittancesInfiniteParams = Omit<SepaRemittancesQueryParams, "page">;

export const useInfiniteSepaRemittancesQuery = (params: SepaRemittancesInfiniteParams) => {
  return useInfiniteQuery({
    queryKey: [...ACCOUNTING_QUERY_KEYS.sepaRemittancesList(params), "infinite"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => accountingService.listSepaRemittances({
      ...params,
      page: Number(pageParam),
    }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metadata?.current_page ?? 1;
      const totalPages = lastPage.metadata?.total_pages ?? 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

export const useSepaRemittanceQuery = (id: string | null) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.sepaRemittanceDetail(id ?? ""),
    queryFn: () => accountingService.getSepaRemittance(id ?? ""),
    enabled: Boolean(id),
  });
};

const invalidateSepaRemittanceCaches = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.sepaRemittances });
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
};

export const useRegenerateSepaRemittance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id, confirmed, dateExecution, paymentLabel,
    }: {
      id: string
      confirmed?: boolean
      dateExecution?: string | null
      paymentLabel?: string
    }) =>
      accountingService.regenerateSepaRemittance(id, { confirmed, dateExecution, paymentLabel }),
    onSuccess: (result) => {
      if (!result.requiresConfirmation) invalidateSepaRemittanceCaches(queryClient);
    },
  });
};

export const useApplySepaBankTreatment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string, input: SepaBankTreatmentInput }) => accountingService.bankTreatment(id, input, true),
    onSuccess: () => {
      invalidateSepaRemittanceCaches(queryClient);
      invalidateLinkedEntityCaches(queryClient);
    },
  });
};

export const useRecordSepaRemittanceEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string, input: SepaRemittanceEventInput }) =>
      accountingService.recordSepaRemittanceEvent(id, input),
    onSuccess: () => invalidateSepaRemittanceCaches(queryClient),
  });
};

export const useRollbackSepaRemittance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountingService.rollbackSepaRemittance(id),
    onSuccess: () => invalidateSepaRemittanceCaches(queryClient),
  });
};

// ── Synthesis ──────────────────────────────────────────

export const useSynthesisQuery = (params: SynthesisQueryParams, enabled = true, keepPrevious = false) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.synthesisList(params),
    queryFn: () => accountingService.getSynthesis(params),
    enabled,
    // Le détail filtré au mois garde l'affichage précédent pendant le chargement.
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
};

export const useSynthesisDetailQuery = (
  params: SynthesisDetailQueryParams | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.synthesisDetail(params ?? { categoryName: "" }),
    queryFn: () => accountingService.getSynthesisDetail(params!),
    enabled: enabled && params !== null,
  });
};

// ── Budgets prévisionnels (ADM-621) ───────────────────────

export const useAccountingBudgetsQuery = (params: AccountingBudgetsQueryParams, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.budgetsList(params),
    queryFn: () => accountingService.getAccountingBudgets(params),
    enabled,
  });
};

// La synthèse lit les budgets : toute écriture doit l'invalider aussi, sinon la
// colonne Budget reste sur l'ancienne valeur jusqu'au prochain refetch.
const invalidateBudgetCaches = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.budgets });
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.synthesis });
};

export const useUpsertAccountingBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AccountingBudgetUpsertPayload) => accountingService.upsertAccountingBudget(payload),
    onSuccess: () => invalidateBudgetCaches(queryClient),
  });
};

export const useDeleteAccountingBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingService.deleteAccountingBudget(id),
    onSuccess: () => invalidateBudgetCaches(queryClient),
  });
};

// ── Paramètres comptables (ADM-621) ───────────────────────

export const useAccountingSettingsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.settings,
    queryFn: () => accountingService.getAccountingSettings(),
    enabled,
  });
};

export const useUpdateAccountingSettingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AccountingSettingsUpdatePayload) => accountingService.updateAccountingSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settings });
      // Les sélecteurs d'exercice hors compta lisent la clôture par cette clé.
      queryClient.invalidateQueries({ queryKey: FISCAL_YEAR_QUERY_KEY });
      // La clôture redéfinit les bornes d'exercice : tout ce qui en dépend
      // (synthèse, budgets) doit être refetché.
      invalidateBudgetCaches(queryClient);
    },
  });
};

// ── Catégories org-scoped (ADM-621) ───────────────────────

export const useOrgOperationTypesQuery = (
  params: { budgetType?: string | null, actif?: boolean } = {},
  enabled = true,
) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypesList(params),
    queryFn: () => accountingService.getOrgOperationTypes(params),
    enabled,
  });
};

// Les catégories vivent dans DEUX caches : la liste org-scoped (page Budgets)
// et la liste mouvement-scopée (drawers). Toute mutation de catégorie doit
// invalider les deux, sinon un compte saisi d'un côté n'apparaît pas de
// l'autre avant un refresh manuel.
export const invalidateOrgOperationTypeCaches = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.orgOperationTypes });
  queryClient.invalidateQueries({ queryKey: [...ACCOUNTING_QUERY_KEYS.ledgerAllocations, "operation-types"] });
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.budgets });
  queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.synthesis });
};

export const useCreateOrgOperationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string, budgetType: string, nature?: "expense" | "revenue" }) => accountingService.createOrgOperationType(payload),
    onSuccess: () => invalidateOrgOperationTypeCaches(queryClient),
  });
};

export const useUpdateOrgOperationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Sans meta, le mutationCache global ne toaste rien : un 422 fermait
    // le dialogue sans un mot.
    // showApiErrorDetail : un 409 « existe déjà » (homonyme après normalisation
    // du nom) doit dire lequel, pas seulement « erreur ».
    meta: { errorMessage: "Erreur lors de la mise à jour de la catégorie", showApiErrorDetail: true },
    mutationFn: (payload: {
      id: string
      nature?: "expense" | "revenue"
      name?: string
      actif?: boolean
      numeroCompte?: string | null
      defaultFor?: "refund" | null
    }) => accountingService.updateOrgOperationType(payload),
    onSuccess: () => invalidateOrgOperationTypeCaches(queryClient),
  });
};

export const usePcgImportMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { errorMessage: "Erreur lors de l'import du plan comptable" },
    mutationFn: (budgetType: string) => accountingService.pcgImportOperationTypes(budgetType),
    onSuccess: () => invalidateOrgOperationTypeCaches(queryClient),
  });
};

export const usePcgRevertMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { errorMessage: "Erreur lors de l'annulation de l'import du plan comptable" },
    mutationFn: (budgetType: string) => accountingService.pcgRevertOperationTypes(budgetType),
    onSuccess: () => invalidateOrgOperationTypeCaches(queryClient),
  });
};

export const useMergeOrgOperationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Sans meta, le mutationCache global ne toaste rien : un 422 fermait
    // le dialogue sans un mot.
    meta: { errorMessage: "Erreur lors de la fusion des catégories" },
    mutationFn: (payload: { sourceId: string, targetId: string }) => accountingService.mergeOrgOperationType(payload),
    onSuccess: () => {
      invalidateOrgOperationTypeCaches(queryClient);
      // Une fusion réécrit les affectations : le grand livre change aussi.
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
    },
  });
};

export const useDeleteOrgOperationTypeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Sans meta, le mutationCache global ne toaste rien : un 422 fermait
    // le dialogue sans un mot.
    meta: { errorMessage: "Erreur lors de la suppression de la catégorie" },
    mutationFn: (operationTypeId: string) => accountingService.deleteOrgOperationType(operationTypeId),
    onSuccess: () => {
      invalidateOrgOperationTypeCaches(queryClient);
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
    },
  });
};

// ── Bank Accounts (Settings) ──────────────────────────────

export const useBankAccountsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bankAccountsList(),
    queryFn: () => accountingService.listBankAccounts(),
    enabled,
  });
};

export const useCreateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BankAccountWritePayload) =>
      accountingService.createBankAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankAccounts });
    },
  });
};

export const useUpdateBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number, payload: Partial<BankAccountWritePayload> }) =>
      accountingService.updateBankAccount(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankAccounts });
    },
  });
};

export const useDeleteBankAccountMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingService.deleteBankAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankAccounts });
    },
  });
};

// Désigner un compte change aussi le statut de l'ancien désigné (ou du repli) :
// on relit toute la liste plutôt que de patcher deux lignes.
export const usePayboxCollectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shouldDesignate }: { id: number, shouldDesignate: boolean }) =>
      shouldDesignate
        ? accountingService.designatePayboxCollection(id)
        : accountingService.clearPayboxCollection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankAccounts });
    },
  });
};

// ── Bridge ──────────────────────────────────────────

export const useBridgeAccountsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bridgeAccountsList(),
    queryFn: () => accountingService.fetchBridgeAccounts(),
    enabled,
  });
};

export const useBridgeProvidersQuery = (q: string, enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.bridgeProvidersList(q),
    queryFn: () => accountingService.fetchBridgeProviders({ q }),
    enabled,
  });
};

export const useBridgeConnectMutation = () => {
  return useMutation({ mutationFn: () => accountingService.bridgeConnect() });
};

export const useBridgeMatchMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bridgeAccountId, bankAccountId }: { bridgeAccountId: number, bankAccountId: number | null }) =>
      accountingService.bridgeMatch(bridgeAccountId, bankAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bridgeAccounts });
    },
  });
};

export const useBridgeRefreshMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bridgeAccountId: number) => accountingService.bridgeRefresh(bridgeAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bridgeAccounts });
    },
  });
};

export const useBridgeReconnectMutation = () => {
  return useMutation({ mutationFn: (bridgeAccountId: number) => accountingService.bridgeReconnect(bridgeAccountId) });
};

export const useBridgeDesyncMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => accountingService.bridgeDesync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bridgeAccounts });
    },
  });
};

export const useBridgeDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => accountingService.bridgeDelete(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bridgeAccounts });
    },
  });
};

// ── Bridge Transfer ──────────────────────────────────────────

export const useBridgeTransferMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bankMovementId: string) => accountingService.bridgeTransfer(bankMovementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

export const useBridgeConfirmMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bankMovementId: string) => accountingService.bridgeConfirm(bankMovementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

/** ADM-864 : libère une initiation Bridge restée sans consentement. */
export const useBridgeReleaseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bankMovementId, discardMovement }: { bankMovementId: string, discardMovement?: boolean }) =>
      accountingService.bridgeRelease(bankMovementId, discardMovement),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    },
  });
};

// ── Entry Templates ──────────────────────────────────

export const useEntryTemplatesQuery = (enabled = true) => {
  return useQuery({
    queryKey: ACCOUNTING_QUERY_KEYS.entryTemplatesList(),
    queryFn: () => accountingService.fetchEntryTemplates(),
    enabled,
  });
};

export const useCreateEntryTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: import("../types/accounting.types").AccountingEntryTemplateCreatePayload) =>
      accountingService.createEntryTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.entryTemplates });
    },
  });
};

export const useDeleteEntryTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountingService.deleteEntryTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.entryTemplates });
    },
  });
};
